import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, FlaskConical, Truck, Boxes, Cpu, Cog, Car, Dumbbell, Factory, Stethoscope, BadgeCheck, Headset, ScanLine, Timer, Package } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { Reveal, Stagger, itemVariant, CountUp } from "../components/Reveal";
import { GranuleField } from "../components/GranuleField";
import { ProcessFlow } from "../components/ProcessFlow";
import { ShoreScale } from "../components/ShoreScale";
import { FaqBlock, faqSchema } from "../components/FaqBlock";
import { BrandStrip } from "../components/BrandStrip";
import { ComparisonTable } from "../components/ComparisonTable";
import { BRANDS, INDUSTRIES, IMAGES, COMPANY, TRUST, TPU_COMPARE } from "../data/content";
import { motion } from "framer-motion";

const ICONS = { Car, Cpu, Cog, Dumbbell, Factory, Stethoscope };
const TRUST_ICONS = { BadgeCheck, Headset, ScanLine, Truck, FlaskConical, Timer, Package, ShieldCheck };

const FAQ = [
  { q: { de: "Was ist TPU (thermoplastisches Polyurethan)?", en: "What is TPU (thermoplastic polyurethane)?" },
    a: { de: "TPU ist ein thermoplastischer Elastomer-Kunststoff, der die Elastizität von Gummi mit der Verarbeitbarkeit von Thermoplasten verbindet. Er bietet hohe Abriebfestigkeit, Flexibilität und gute Chemikalienbeständigkeit.", en: "TPU is a thermoplastic elastomer that combines the elasticity of rubber with the processability of thermoplastics. It offers high abrasion resistance, flexibility and good chemical resistance." } },
  { q: { de: "Liefert PlastoNord TPU als Neuware und Regranulat?", en: "Does PlastoNord supply TPU as virgin material and regranulate?" },
    a: { de: "Ja. Wir liefern transparente und natur Neuware, FR-Typen, Masterbatch sowie laborgeprüftes Regranulat aus Neuware mit vollständiger Chargenrückverfolgbarkeit.", en: "Yes. We supply transparent and natural virgin material, FR grades, masterbatch and lab-tested regranulate from virgin material with full batch traceability." } },
  { q: { de: "Für welche Verfahren ist Ihr TPU geeignet?", en: "Which processes is your TPU suitable for?" },
    a: { de: "Unsere TPU-Granulate eignen sich für Extrusion (Schläuche, Profile, Folien, Kabel) und Spritzguss technischer Bauteile. Die Auswahl richtet sich nach Shore-Härte und MFI.", en: "Our TPU granules are suitable for extrusion (tubing, profiles, films, cables) and injection molding of technical parts. Selection depends on Shore hardness and MFI." } },
  { q: { de: "Welche Shore-Härten sind verfügbar?", en: "Which Shore hardness ranges are available?" },
    a: { de: "Typischerweise von 60 Shore A (sehr weich) bis 95 Shore A (hart). Oberhalb von 95 A wird die Härte in Shore D angegeben – bis ca. 80 Shore D für starre technische Bauteile. Standardtypen umfassen 85 A, 90 A und 95 A.", en: "Typically from 60 Shore A (very soft) to 95 Shore A (hard). Above 95 A, hardness is expressed in Shore D – up to approx. 80 Shore D for rigid technical parts. Standard grades include 85 A, 90 A and 95 A." } },
];

const STATS = [
  { to: 80, suffix: " Shore D", prefix: "60 A – ", de: "Härtebereich (A & D)", en: "Hardness range (A & D)" },
  { to: 1000, suffix: "+", de: "Laborprüfungen", en: "lab tests performed" },
  { to: 50, suffix: "+", de: "Materialfamilien", en: "material families" },
  { to: 100, suffix: "%", de: "Chargenrückverfolgbarkeit", en: "batch traceability" },
];

export default function Home() {
  const { t, lang, pick } = useLang();
  return (
    <>
      <Seo
        title="PlastoNord GmbH – TPU Granulat & technische Kunststoffe | Hersteller Deutschland"
        description="PlastoNord liefert technische Kunststoff-Rohstoffe mit Spezialisierung auf TPU (thermoplastisches Polyurethan) für Extrusion, Spritzguss und Industrie. Neuware, FR-Typen & Regranulat – made in Germany."
        path="/"
        schema={faqSchema(FAQ, pick)}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#e4e9f0] bg-gradient-to-b from-[#f6f8fb] to-white grain">
        <div className="absolute inset-0"><GranuleField density={28} /></div>
        <div className="pn-container relative grid items-center gap-12 py-24 md:py-32 lg:grid-cols-2">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#e4e9f0] bg-white/70 px-4 py-1.5 text-xs font-medium text-[#16181D] backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-[#E2611C]" /> {t("common.madeIn")} · TPU Spezialist
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-head text-4xl font-light leading-[1.04] tracking-tight text-[#16181D] md:text-6xl">
                {lang === "de" ? <>Technische Kunststoffe – <span className="text-gradient font-normal">Schwerpunkt TPU</span></> : <>Technical plastics – <span className="text-gradient font-normal">with a focus on TPU</span></>}
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#4a4d52]">
                {lang === "de"
                  ? "PlastoNord liefert ein breites Sortiment technischer Kunststoff-Rohstoffe – mit einem klaren Schwerpunkt auf thermoplastischem Polyurethan (TPU) für Extrusion, Spritzguss und industrielle Anwendungen."
                  : "PlastoNord supplies a broad range of technical plastic raw materials – with a clear focus on thermoplastic polyurethane (TPU) for extrusion, injection molding and industrial applications."}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/tpu" className="btn-primary" data-testid="hero-tpu-cta">{t("cta.explore")} <ArrowRight size={17} /></Link>
                <Link to="/kontakt" className="btn-ghost" data-testid="hero-contact-cta"><Phone size={16} /> {t("cta.request")}</Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative">
            <div className="overflow-hidden rounded-2xl border border-[#e4e9f0] shadow-2xl shadow-[#16181D]/10">
              <img src={IMAGES.hero} alt="Echtes transparentes NORDFLEX® TPU Granulat (Neuware) von PlastoNord – Makroaufnahme" className="h-full w-full object-cover" loading="eager" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-[#e4e9f0] bg-white/90 p-4 shadow-xl backdrop-blur sm:block animate-floaty">
              <p className="font-mono text-xs text-[#E2611C]">NORDFLEX® TPU</p>
              <p className="font-head text-lg font-semibold text-[#16181D]">60 A – 80 D</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-[#e4e9f0] bg-white">
        <Stagger className="pn-container grid grid-cols-2 gap-6 py-12 md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div key={i} variants={itemVariant} className="text-center md:text-left">
              <p className="font-head text-3xl font-semibold text-[#16181D] md:text-4xl">
                {s.prefix}<CountUp to={s.to} suffix={s.suffix} />
              </p>
              <p className="mt-1 text-sm text-[#4a4d52]">{lang === "de" ? s.de : s.en}</p>
            </motion.div>
          ))}
        </Stagger>
      </section>

      <BrandStrip />

      {/* COMPANY / FACILITY (real building) */}
      <section className="py-24" id="unternehmen">
        <div className="pn-container grid items-center gap-14 lg:grid-cols-2">
          <Reveal className="relative">
            <div className="overflow-hidden rounded-2xl border border-[#e4e9f0] shadow-2xl shadow-[#16181D]/10">
              <img src={IMAGES.facility} alt="PlastoNord GmbH – Bürogebäude mit Produktion und Lager in Loxstedt bei Bremerhaven" className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-5 -right-5 hidden rounded-xl border border-[#e4e9f0] bg-white/90 p-4 shadow-xl backdrop-blur sm:block">
              <p className="font-mono text-xs text-[#E2611C]">Loxstedt · A27</p>
              <p className="font-head text-lg font-semibold text-[#16181D]">{lang === "de" ? "Firmensitz" : "Headquarters"}</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="overline">{lang === "de" ? "Unser Standort" : "Our location"}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">
              {lang === "de" ? "Eigene Produktion & Lager in Norddeutschland" : "Own production & warehouse in northern Germany"}
            </h2>
            <p className="mt-5 max-w-xl leading-relaxed text-[#4a4d52]">
              {lang === "de"
                ? "Von unserem Firmensitz im Industriegebiet Stotel bei Bremerhaven – direkt an der A27 – steuern wir Produktion, Labor und Logistik. Moderne Anlagen, eigene Photovoltaik und kurze Wege sichern konstante Qualität und zuverlässige Lieferungen."
                : "From our headquarters in the Stotel industrial area near Bremerhaven – directly on the A27 – we manage production, laboratory and logistics. Modern facilities, our own photovoltaics and short distances ensure consistent quality and reliable deliveries."}
            </p>
            <dl className="mt-8 grid grid-cols-3 gap-4">
              {[["A27", lang === "de" ? "direkte Anbindung" : "direct access"], [lang === "de" ? "Eigenes" : "Own", lang === "de" ? "Labor & QC" : "lab & QC"], ["PV", lang === "de" ? "Solarstrom" : "solar power"]].map(([k, v]) => (
                <div key={v} className="rounded-xl border border-[#e4e9f0] p-4">
                  <dt className="font-head text-xl font-semibold text-[#16181D]">{k}</dt>
                  <dd className="mt-1 text-xs text-[#4a4d52]">{v}</dd>
                </div>
              ))}
            </dl>
            <Link to="/ueber-uns" className="btn-ghost mt-8 !px-5 !py-2 text-sm" data-testid="company-cta">{lang === "de" ? "Über PlastoNord" : "About PlastoNord"} <ArrowRight size={15} /></Link>
          </Reveal>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-24">
        <div className="pn-container">
          <Reveal><p className="overline">{lang === "de" ? "Warum PlastoNord" : "Why PlastoNord"}</p>
            <h2 className="mt-3 max-w-2xl font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">
              {lang === "de" ? "Verlässlichkeit auf jeder Ebene" : "Reliability at every level"}
            </h2></Reveal>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map((it) => { const Icon = TRUST_ICONS[it.icon]; const c = lang === "de" ? it.de : it.en; return (
              <motion.div key={c.t} variants={itemVariant} className="pn-card p-6">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#FBEADD] text-[#E2611C]"><Icon size={20} /></span>
                <h3 className="mt-4 font-head text-base font-semibold text-[#16181D]">{c.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[#4a4d52]">{c.d}</p>
              </motion.div>
            ); })}
          </Stagger>
        </div>
      </section>
      <section className="py-24" id="produkte">
        <div className="pn-container">
          <Reveal><p className="overline">{t("nav.products")}</p>
            <h2 className="mt-3 max-w-2xl font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">
              {lang === "de" ? "Unsere Materialfamilien im Überblick" : "Our material families at a glance"}
            </h2></Reveal>
          <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {BRANDS.map((b) => {
              const c = pick(b);
              return (
                <motion.div key={b.sku} variants={itemVariant}>
                  <Link to={b.href} data-testid={`brand-${b.sku}`} className={`pn-card relative block h-full p-7 ${b.primary ? "ring-1 ring-[#E2611C]" : ""}`}>
                    {b.primary && <span className="absolute right-5 top-5 rounded-full bg-[#FBEADD] px-3 py-1 text-xs font-semibold text-[#16181D]">{lang === "de" ? "Hauptfokus" : "Main focus"}</span>}
                    <p className="font-mono text-xs text-[#E2611C]">{b.material}</p>
                    <h3 className="mt-2 font-head text-xl font-semibold text-[#16181D]">{c.name}</h3>
                    <p className="text-sm text-[#4a4d52]">{c.tagline}</p>
                    <p className="mt-4 text-sm leading-relaxed text-[#4a4d52]">{c.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#16181D]">{t("cta.more")} <ArrowRight size={15} /></span>
                  </Link>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* TPU FOCUS */}
      <section className="bg-[#16181D] py-24 text-white grain" id="tpu">
        <div className="pn-container grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="overline">TPU · NORDFLEX®</p>
            <h2 className="mt-3 font-head text-3xl font-light tracking-tight md:text-5xl">
              {lang === "de" ? "Thermoplastisches Polyurethan – unser strategischer Fokus" : "Thermoplastic polyurethane – our strategic focus"}
            </h2>
            <p className="mt-6 max-w-xl leading-relaxed text-white/70">
              {lang === "de"
                ? "Hohe Abriebfestigkeit, Elastizität und Beständigkeit gegen Öle, Fette und viele Chemikalien. Verfügbar als transparente und natur Neuware, FR-Typen, Masterbatch und Regranulat."
                : "High abrasion resistance, elasticity and resistance to oils, greases and many chemicals. Available as transparent and natural virgin material, FR grades, masterbatch and regranulate."}
            </p>
            <ul className="mt-7 grid grid-cols-2 gap-3 text-sm text-white/80">
              {["Extrusion", "Spritzguss / Injection", "Schläuche & Folien", "Kabel & Profile", lang === "de" ? "und vieles mehr" : "and much more"].map((x) => (
                <li key={x} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#E2611C]" /> {x}</li>
              ))}
            </ul>
            <Link to="/tpu" className="btn-primary mt-9 !bg-[#E2611C]" data-testid="tpu-focus-cta">{t("cta.explore")} <ArrowRight size={17} /></Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
              <p className="text-sm text-white/60">{lang === "de" ? "TPU Verarbeitungsprozess" : "TPU processing flow"}</p>
              <div className="mt-4 rounded-xl bg-white p-4">
                <ProcessFlow mode="extrusion" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SHORE */}
      <section className="py-24" id="shore">
        <div className="pn-container grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="overline">{t("common.hardness")}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">
              {lang === "de" ? "Shore-Härte von weich bis hart" : "Shore hardness from soft to hard"}
            </h2>
            <p className="mt-5 max-w-lg leading-relaxed text-[#4a4d52]">
              {lang === "de" ? "Wählen Sie eine Shore-Härte und entdecken Sie typische Anwendungen – von flexiblen Dichtungen bis zu belasteten technischen Bauteilen." : "Select a Shore hardness and discover typical applications – from flexible seals to loaded technical parts."}
            </p>
          </Reveal>
          <Reveal delay={0.15}><ShoreScale lang={lang} /></Reveal>
        </div>
      </section>

      {/* TPU COMPARISON (AI-search asset) */}
      <section className="bg-[#f6f8fb] py-24">
        <div className="pn-container">
          <Reveal><p className="overline">{lang === "de" ? "Vergleich" : "Comparison"}</p>
            <h2 className="mt-3 max-w-2xl font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "TPU für Extrusion vs. Spritzguss" : "TPU for extrusion vs. injection molding"}</h2>
            <p className="mt-4 max-w-2xl text-[#4a4d52]">{lang === "de" ? "Welcher TPU-Typ passt zu welchem Verfahren? Ein kompakter technischer Überblick für Einkäufer und Verarbeiter." : "Which TPU grade suits which process? A compact technical overview for buyers and processors."}</p>
          </Reveal>
          <div className="mt-10"><ComparisonTable data={TPU_COMPARE} caption="TPU Extrusion vs Spritzguss" /></div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-[#f6f8fb] py-24">
        <div className="pn-container">
          <Reveal><p className="overline">{t("nav.industries")}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "Branchen, die wir beliefern" : "Industries we supply"}</h2></Reveal>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind) => {
              const Icon = ICONS[ind.icon];
              const c = pick(ind);
              return (
                <motion.div key={c.t} variants={itemVariant} className="pn-card p-6">
                  <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#FBEADD] text-[#16181D]"><Icon size={20} /></span>
                  <h3 className="mt-4 font-head text-lg font-semibold text-[#16181D]">{c.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#4a4d52]">{c.d}</p>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* QUALITY + LOGISTICS teasers */}
      <section className="py-24">
        <div className="pn-container grid gap-6 md:grid-cols-2">
          <Reveal>
            <Link to="/qualitaet" className="pn-card group block overflow-hidden">
              <img src={IMAGES.lab} alt="TPU Qualitätskontrolle im Labor von PlastoNord" className="h-56 w-full object-cover" loading="lazy" />
              <div className="p-7">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#FBEADD] text-[#16181D]"><FlaskConical size={19} /></span>
                <h3 className="mt-4 font-head text-2xl font-medium text-[#16181D]">{t("nav.quality")}</h3>
                <p className="mt-2 text-[#4a4d52]">{lang === "de" ? "Eigenes Labor: MFI, DSC, Shore, Feuchte, Zug- und Schlagprüfung – dokumentiert und rückverfolgbar." : "In-house lab: MFI, DSC, Shore, moisture, tensile and impact testing – documented and traceable."}</p>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/logistik" className="pn-card group block overflow-hidden">
              <img src={IMAGES.facility} alt="Logistik und Lager für Kunststoffgranulat bei PlastoNord" className="h-56 w-full object-cover" loading="lazy" />
              <div className="p-7">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#FBEADD] text-[#16181D]"><Truck size={19} /></span>
                <h3 className="mt-4 font-head text-2xl font-medium text-[#16181D]">{t("nav.logistics")}</h3>
                <p className="mt-2 text-[#4a4d52]">{lang === "de" ? "Big Bag, Octabin, 25-kg-Säcke, palettiert – zuverlässige Lieferung weltweit." : "Big bag, octabin, 25 kg sacks, palletized – reliable delivery worldwide."}</p>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PORTAL CTA */}
      <section className="pb-24">
        <div className="pn-container">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-[#16181D] p-10 text-white md:p-16 grain">
              <div className="absolute right-0 top-0 h-full w-1/2 dot-grid opacity-20" />
              <div className="relative max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium"><Boxes size={14} /> {lang === "de" ? "Jetzt verfügbar" : "Now available"}</span>
                <h2 className="mt-5 font-head text-3xl font-light tracking-tight md:text-5xl">{t("nav.portal")}</h2>
                <p className="mt-4 max-w-xl text-white/70">{lang === "de" ? "Kundenspezifische Produktsichtbarkeit, Verfügbarkeiten, Datenblätter, Zertifikate und Musteranfragen – unter portal.plasto-nord.com." : "Customer-specific product visibility, availability, data sheets, certificates and sample requests – at portal.plasto-nord.com."}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a href={COMPANY.portalUrl} target="_blank" rel="noopener noreferrer" className="btn-primary !bg-[#E2611C]" data-testid="portal-cta">{t("cta.portal")} <ArrowRight size={17} /></a>
                  <Link to="/kontakt" className="btn-ghost !border-white/40 !text-white hover:!bg-white hover:!text-[#16181D]">{t("cta.contact")}</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqBlock items={FAQ} />
    </>
  );
}
